import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "sonner";
import { copyLink } from "../lib/inviteToast";
import { AlertTriangle, Check, CheckCircle2, Download, FileSpreadsheet, Loader2, Mail, RefreshCw, Upload, X } from "lucide-react";
import { API, formatApiError } from "../auth/api";

/**
 * 2026-10-25: Én fælles import af stamdata (chauffører, biler, kunder, priser,
 * lokationer, containere, brugere ...). Hent skabelonen -> vælg filen eller
 * indsæt rækkerne fra Excel -> kontrol pr. række -> importér. Intet gemmes før
 * "Importér". Findes en post i forvejen, opdateres den (samme fil kan uploades igen).
 *
 * kind: importens type (se _IMPORTS i server.py). companyId: kun super admin
 * (import for et firma). mail: ture fra en mail (fx Argo) - tekst + dato/lokation.
 */
const card = "bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm";
const btnPrimary = "inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50";
const btnGhost = "inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 text-sm font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50";
const input = "w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-sm";
const ACTION = { create: ["Ny", "bg-emerald-100 text-emerald-800"], update: ["Opdateres", "bg-sky-100 text-sky-800"], skip: ["Findes", "bg-slate-100 text-slate-600"],
  // 2026-10-26: Prisaftaler - en aftale kan stå på flere rækker (samme Aftale-ID og dato).
  part: ["Samme aftale", "bg-slate-50 text-slate-500 border border-slate-200"] };

async function errText(e, fallback) {
  let d = e?.response?.data;
  if (d instanceof Blob) { try { d = JSON.parse(await d.text()); } catch (_) { d = null; } }
  const det = d?.detail;
  return (typeof det === "string" ? det : det?.message) || formatApiError(e) || fallback;
}

export default function ImportDialog({ kind, title, companyId = "", onClose, onDone, mail = null, invitePeople = false }) {
  const [file, setFile] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [check, setCheck] = useState(null);     // {rows, counts}
  const [result, setResult] = useState(null);   // {message, errors, new_user_ids}
  const [day, setDay] = useState(mail?.date || "");
  const [plads, setPlads] = useState("");
  const [cols, setCols] = useState(null);       // kolonnerne med format og eksempel
  const [exp, setExp] = useState({ exportable: false, note: "" });   // 2026-10-25: "Hent nuværende data"
  useEffect(() => {
    if (mail) return;
    axios.get(`${API}/import/${kind}/columns`, { params: companyId ? { company_id: companyId } : {} })
      .then((r) => { setCols(r.data.cols || []); setExp({ exportable: !!r.data.exportable, note: r.data.export_note || "" }); })
      .catch(() => setCols([]));
  }, [kind, companyId, mail]);

  const template = async (what = "template") => {
    try {
      const r = await axios.get(`${API}/import/${kind}/${what}`, { params: companyId ? { company_id: companyId } : {}, responseType: "blob" });
      const m = /filename="([^"]+)"/.exec(r.headers?.["content-disposition"] || "");
      const { saveAs } = await import("file-saver");
      saveAs(r.data, m ? m[1] : `${what === "export" ? "data" : "import"}-${kind}.xlsx`);
    } catch (e) { toast.error(await errText(e, what === "export" ? "Kunne ikke hente data" : "Kunne ikke hente skabelonen")); }
  };

  const send = async (dry) => {
    setBusy(true);
    try {
      let r;
      if (mail) {
        r = await axios.post(`${API}/tours/import-text`, { text, date: day, plads, dry_run: dry, label: "mail" });
        if (dry) r.data.counts = { create: r.data.valid, update: 0, skip: 0, error: r.data.invalid };
      } else {
        const fd = new FormData();
        if (file) fd.append("file", file);
        else fd.append("text", text);
        if (companyId) fd.append("company_id", companyId);
        r = await axios.post(`${API}/import/${kind}`, fd, { params: { dry_run: dry }, headers: { "Content-Type": "multipart/form-data" } });
      }
      if (dry) { setCheck(r.data); return; }
      setResult(r.data);
      setCheck(null);
      toast.success(r.data.message || "Importeret");
      onDone && onDone();
    } catch (e) { toast.error(await errText(e, "Importen fejlede")); }
    finally { setBusy(false); }
  };

  const invite = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/import/invite`, { user_ids: result.new_user_ids });
      const failed = r.data.failed || [];
      if (r.data.sent) toast.success(`Invitation sendt til ${r.data.sent}`);
      // 2026-10-26: Mails, der ikke kunne sendes, står med et link, der kan kopieres.
      if (failed.length) toast.error(`${failed.length} ${failed.length === 1 ? "mail" : "mails"} blev ikke sendt${failed[0].error ? ` (${failed[0].error})` : ""}`, { duration: 15000 });
      setResult({ ...result, new_user_ids: [], invite_failed: failed });
    } catch (e) { toast.error(await errText(e, "Kunne ikke sende invitationer")); }
    finally { setBusy(false); }
  };

  const ready = mail ? text.trim() : (file || text.trim());
  const n = check ? check.counts.create + check.counts.update : 0;
  return createPortal(
    <div className="fixed inset-0 z-[95] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className={`${card} w-full max-w-2xl p-5 space-y-4 max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()} data-testid={`import-dialog-${kind}`}>
        <div className="flex items-center gap-2">
          {mail ? <Mail className="w-5 h-5 text-emerald-600" /> : <FileSpreadsheet className="w-5 h-5 text-emerald-600" />}
          <div className="font-bold text-lg flex-1">{title}</div>
          <button onClick={onClose} aria-label="Luk" className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700"><X className="w-5 h-5" /></button>
        </div>

        {!result && (
          <>
            {mail ? (
              <div className="text-sm text-slate-600 dark:text-slate-300 space-y-1">
                <p>Kopiér tabellen med turene fra mailen (med overskrifterne) og indsæt den her. Hver række kontrolleres som i Excel-skabelonen: på en kundes genbrugsplads bliver turen kundens bestilling.</p>
                <p>Samme ordrenr. eller samme container samme dag oprettes aldrig to gange - mailen kan trygt indsættes igen.</p>
              </div>
            ) : (
              <ol className="text-sm text-slate-600 dark:text-slate-300 list-decimal pl-5 space-y-0.5">
                <li>Hent skabelonen og udfyld den - eller kopiér rækkerne fra jeres eget Excel (første række = kolonnenavnene).</li>
                <li>Vælg filen eller indsæt rækkerne.</li>
                <li>Kontrollér: hver række vises som ny, opdateres, findes eller fejl. Intet gemmes, før du trykker Importér.</li>
              </ol>
            )}
            {!mail && cols && cols.length > 0 && (
              <details className="rounded-xl border border-slate-200 dark:border-slate-700" data-testid="import-columns" open={cols.length <= 6}>
                <summary className="px-3 py-2 text-sm font-semibold cursor-pointer">Kolonner ({cols.filter((c) => c.required).length} skal udfyldes) - datoer DD-MM-ÅÅÅÅ, tider TT:MM</summary>
                <div className="px-3 pb-2 divide-y divide-slate-100 dark:divide-slate-700">
                  {cols.map((c) => (
                    <div key={c.header} className="py-1.5 text-xs grid grid-cols-[minmax(110px,30%)_1fr] gap-2">
                      <span className="font-semibold">{c.header}{c.required ? " *" : ""}</span>
                      <span className="text-slate-600 dark:text-slate-300">{c.help}{c.example ? <span className="text-slate-400">{c.help ? " · " : ""}fx {c.example}</span> : null}</span>
                    </div>
                  ))}
                </div>
              </details>
            )}
            {!mail && (
              <div className="flex flex-wrap items-center gap-2">
                <button className={btnGhost} onClick={() => template()} data-testid="import-template"><Download className="w-4 h-4" /> Hent skabelon</button>
                {exp.exportable && <button className={btnGhost} onClick={() => template("export")} data-testid="import-export" title={exp.note}><Download className="w-4 h-4" /> Hent nuværende data</button>}
                <label className={`${btnGhost} cursor-pointer`}>
                  <Upload className="w-4 h-4" /> {file ? file.name : "Vælg fil (.xlsx / .csv)"}
                  <input type="file" accept=".xlsx,.csv,.txt" className="hidden" data-testid="import-file"
                    onChange={(e) => { setFile(e.target.files?.[0] || null); setText(""); setCheck(null); }} />
                </label>
                {file && <button className="text-xs text-slate-500 underline" onClick={() => setFile(null)}>Fjern filen</button>}
              </div>
            )}
            {!mail && exp.note && <p className="text-xs text-slate-500" data-testid="import-export-note">{exp.exportable ? `"Hent nuværende data" giver jeres data i skabelonen - ret og upload igen. ${exp.note}` : exp.note}</p>}
            {mail && (
              <div className="grid sm:grid-cols-2 gap-2">
                <div><span className="block text-xs font-semibold text-slate-500 mb-1">Dato, hvis mailen ikke har den</span>
                  <input type="date" className={input} value={day} onChange={(e) => { setDay(e.target.value); setCheck(null); }} data-testid="import-mail-date" /></div>
                <div><span className="block text-xs font-semibold text-slate-500 mb-1">Lokation</span>
                  <select className={input} value={plads} onChange={(e) => { setPlads(e.target.value); setCheck(null); }} data-testid="import-mail-plads">
                    <option value="">Fra mailen</option>
                    {(mail.sites || []).map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select></div>
              </div>
            )}
            {!file && (
              <textarea className={`${input} font-mono text-xs h-36`} value={text} data-testid="import-text"
                placeholder={mail ? "Indsæt tabellen fra mailen her" : "...eller indsæt rækkerne fra Excel her (marker cellerne inkl. overskrifterne, kopiér, indsæt)"}
                onChange={(e) => { setText(e.target.value); setCheck(null); }} />
            )}
          </>
        )}

        {check && (
          <div className="space-y-2" data-testid="import-check">
            <div className="flex flex-wrap gap-2 text-xs font-semibold">
              <span className="px-2 py-1 rounded-full bg-emerald-100 text-emerald-800">{check.counts.create} nye</span>
              {check.counts.update > 0 && <span className="px-2 py-1 rounded-full bg-sky-100 text-sky-800">{check.counts.update} opdateres</span>}
              {check.counts.skip > 0 && <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-700">{check.counts.skip} findes allerede</span>}
              {check.counts.error > 0 && <span className="px-2 py-1 rounded-full bg-red-100 text-red-800">{check.counts.error} med fejl (springes over)</span>}
              {check.format && <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-600">Læst som {check.format}</span>}
            </div>
            <div className="max-h-72 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-700 border border-slate-100 dark:border-slate-700 rounded-xl">
              {check.rows.map((r) => {
                const [lbl, cls] = r.ok ? (ACTION[r.action || "create"] || ACTION.create) : ["Fejl", "bg-red-100 text-red-800"];
                return (
                  <div key={r.row} className="px-3 py-2 text-sm" data-testid={`import-row-${r.row}`}>
                    <div className="flex items-start gap-2">
                      <span className="text-xs text-slate-400 w-12 shrink-0 pt-0.5">Række {r.row}</span>
                      <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded shrink-0 ${cls}`}>{lbl}</span>
                      <span className="min-w-0 break-words">{r.text || "–"}</span>
                    </div>
                    {r.errors.map((x, i) => <div key={i} className="ml-14 text-xs text-red-700 flex gap-1"><X className="w-3 h-3 mt-0.5 shrink-0" />{x}</div>)}
                    {(r.warnings || []).map((x, i) => <div key={`w${i}`} className="ml-14 text-xs text-amber-700 flex gap-1"><AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />{x}</div>)}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {result && (
          <div className="space-y-2" data-testid="import-result">
            <div className="flex items-center gap-2 text-emerald-800 font-semibold"><CheckCircle2 className="w-5 h-5" /> {result.message}</div>
            {/* 2026-10-26: Mail fra en ekstern kunde (fx Vestforbrænding) - nye materialer og containere. */}
            {result.created_anlaeg > 0 && (
              <div className="text-sm text-sky-800" data-testid="import-created-anlaeg">
                {result.created_anlaeg} {result.created_anlaeg === 1 ? "nyt modtageanlæg" : "nye modtageanlæg"} hos kunden - tjek adressen under Lokationer › Eksterne kunder.
              </div>
            )}
            {result.created_master && (result.created_master.material + result.created_master.container) > 0 && (
              <div className="text-sm text-sky-800" data-testid="import-created-master">
                Oprettet hos kunden: {result.created_master.material} {result.created_master.material === 1 ? "materiale" : "materialer"} og {result.created_master.container} {result.created_master.container === 1 ? "container" : "containere"} - de genkendes næste gang.
              </div>
            )}
            {(result.errors || []).length > 0 && (
              <div className="text-xs text-red-700 space-y-0.5">{result.errors.map((x) => <div key={x.row}>Række {x.row}: {x.reason}</div>)}</div>
            )}
            {invitePeople && (result.new_user_ids || []).length > 0 && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-900/20 p-3 text-sm space-y-2">
                <div>De {result.new_user_ids.length} nye har ikke fået en invitation endnu. Send den, når de skal i gang (fx når chaufførerne skal begynde at stemple) - det kan også gøres senere ud for hver person.</div>
                <button className={btnPrimary} disabled={busy} onClick={invite} data-testid="import-invite"><Mail className="w-4 h-4" /> Send invitation til {result.new_user_ids.length} nu</button>
              </div>
            )}
            {(result.invite_failed || []).length > 0 && (
              <div className="rounded-xl border border-red-200 bg-red-50 dark:bg-red-900/20 p-3 text-sm space-y-2" data-testid="import-invite-failed">
                <div className="font-semibold text-red-800">Mailen blev ikke sendt til {result.invite_failed.length} - send linket selv (SMS eller mail). Det gælder i 72 timer.</div>
                {result.invite_failed.map((f) => (
                  <div key={f.email} className="flex items-center gap-2">
                    <span className="flex-1 min-w-0 truncate">{f.name} · {f.email}</span>
                    {f.link && <button className={btnGhost} onClick={() => copyLink(f.link)}>Kopiér link</button>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button className={btnGhost} onClick={onClose}>{result ? "Luk" : "Annuller"}</button>
          {!result && <button className={btnGhost} disabled={busy || !ready} onClick={() => send(true)} data-testid="import-check-btn">
            {busy && !check ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Kontrollér</button>}
          {!result && check && <button className={btnPrimary} disabled={busy || n === 0} onClick={() => send(false)} data-testid="import-run">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {mail ? `Opret ${n} ture` : kind === "prices" ? `Importér ${n} ${n === 1 ? "aftale" : "aftaler"}` : `Importér ${n} ${n === 1 ? "række" : "rækker"}`}</button>}
        </div>
      </div>
    </div>, document.body);
}
